import { fr, enUS, Locale } from 'date-fns/locale';
import { parse, format } from 'date-fns';

/**
 * Date avec heure SANS fuseau (`AAAA-MM-JJ HH:mm[:ss]`, espace ou `T`) : heure
 * « murale », déjà exprimée dans le fuseau de l'utilisateur. C'est l'`id` des
 * champs date + heure renvoyés par le back (`DateCast`).
 */
const WALL_CLOCK_DATETIME_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

/** Date avec heure ISO 8601 AVEC fuseau (`Z` ou `±HH:mm`) : champs système. */
const ZONED_DATETIME_PATTERN =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/;

/** Gabarit « date complète avec heure » : « mardi 29 septembre 2026 à 14:57 ». */
const FULL_DATE_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  dateStyle: 'full',
  timeStyle: 'short',
};

/** Fuseau neutre qui restitue une heure murale telle quelle. */
const WALL_CLOCK_TIME_ZONE = 'UTC';

export class DatesUtils {
  /**
   * Les champs date/datetime sont sérialisés par le back en objet
   * `{ id, display }` (comme les selects). Ces deux helpers sont tolérants :
   * ils acceptent l'objet OU une string (rétro-compat / champs système).
   */

  /** Libellé affichable d'une valeur date (`{ id, display }` ou string). */
  public static dateDisplay(value: any): string {
    if (value == null) return '';
    if (typeof value === 'object') return String(value.display ?? '');
    return String(value);
  }

  /** Valeur machine d'une date (`id` de l'objet, ou la string telle quelle). */
  public static dateValue(value: any): any {
    if (value == null) return value;
    if (typeof value === 'object') return value.id ?? null;
    return value;
  }

  /**
   * Valeur machine d'une date QUI PORTE UNE HEURE, sinon `null`.
   *
   * Accepte l'objet `{ id, display }` d'un champ date, une chaîne (ISO ou
   * `AAAA-MM-JJ HH:mm:ss`) ou un `Date`. Une date seule (`AAAA-MM-JJ`) et
   * toute autre valeur (nombre, select, texte) renvoient `null`.
   */
  public static dateTimeValue(value: unknown): string | null {
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value.toISOString();
    }
    const raw = DatesUtils.dateValue(value);
    if (typeof raw !== 'string') return null;
    return WALL_CLOCK_DATETIME_PATTERN.test(raw) ||
      ZONED_DATETIME_PATTERN.test(raw)
      ? raw
      : null;
  }

  /**
   * Date complète avec heure dans la langue donnée — « mardi 29 septembre
   * 2026 à 14:57 » en français, « Tuesday, September 29, 2026 at 2:57 PM » en
   * anglais. `null` si la valeur ne porte pas d'heure.
   *
   * Une heure murale est restituée telle quelle, sans conversion (le back l'a
   * déjà exprimée dans le fuseau de l'utilisateur) ; une date ISO avec fuseau
   * est affichée dans le fuseau du navigateur, comme le fait le pipe `date`.
   */
  public static fullDateTime(value: unknown, locale: string): string | null {
    const raw = DatesUtils.dateTimeValue(value);
    if (raw === null) return null;

    const wallClock = WALL_CLOCK_DATETIME_PATTERN.exec(raw);
    if (wallClock) {
      const [year, month, day, hours, minutes, seconds] = wallClock
        .slice(1, 7)
        .map((part) => Number(part ?? 0));
      const instant = Date.UTC(year, month - 1, day, hours, minutes, seconds);
      return new Intl.DateTimeFormat(locale, {
        ...FULL_DATE_TIME_FORMAT,
        timeZone: WALL_CLOCK_TIME_ZONE,
      }).format(instant);
    }

    const instant = new Date(raw);
    return isNaN(instant.getTime())
      ? null
      : new Intl.DateTimeFormat(locale, FULL_DATE_TIME_FORMAT).format(instant);
  }

  public static returnDateFormatFromLocale(
    locale: string,
    date: string,
    fromSQLdate = false
  ) {
    if (date && date.includes('T')) {
      const splitAtT = date.split('T');
      date = splitAtT[0];
    }
    if (!fromSQLdate) {
      if (date) {
        const sliptedDate = date.split('/');
        // à éditer plus tard si on ajoute des locales
        return `${sliptedDate[1]}/${sliptedDate[0]}/${sliptedDate[2]}`;
      }
    }
    if (fromSQLdate) {
      if (date === null || date === undefined || date === '') {
        return date;
      }
      const splitAtSpaceDate = date.split(' ');
      const sliptedDate = splitAtSpaceDate[0].split('-');
      if (locale === 'fr') {
        return `${sliptedDate[2]}/${sliptedDate[1]}/${sliptedDate[0]}`;
      }
      return `${sliptedDate[1]}/${sliptedDate[2]}/${sliptedDate[0]}`;
    }
    return date;
  }

  public static returnSqlDateFormat(locale: string, date: string) {
    const sliptedDate = date.split('/');
    if (locale === 'fr') {
      return `${sliptedDate[2]}-${sliptedDate[1]}-${sliptedDate[0]}`;
    }
    return `${sliptedDate[2]}-${sliptedDate[0]}-${sliptedDate[1]}`;
  }

  /**
   * Cette chaîne est-elle une date ? Deux écritures sont acceptées, la SQL
   * `AAAA-MM-JJ` et la française `JJ/MM/AAAA`.
   *
   * La validation porte sur les COMPOSANTES du calendrier, non sur
   * `Date.parse` : celui-ci ne sait lire que l'ISO, tout le reste retombant sur
   * son analyseur historique, qui lit à l'AMÉRICAINE et qui reporte les
   * débordements.
   */
  public static isDateString(str: any) {
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$|^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateRegex.exec(String(str));
    if (!match) return false;

    const [annee, mois, jour] = match[1]
      ? [match[1], match[2], match[3]].map(Number)
      : [match[6], match[5], match[4]].map(Number);

    // Le constructeur reporte un jour hors du mois sur le mois suivant : les
    // composantes relues sont le seul témoin que la date existe vraiment.
    const date = new Date(annee, mois - 1, jour);
    return (
      date.getFullYear() === annee &&
      date.getMonth() === mois - 1 &&
      date.getDate() === jour
    );
  }

  public static convertDate(
    locale: string,
    dates: string | string[],
    SQLdate = false
  ) {
    if (Array.isArray(dates)) {
      const fDates = [] as string[];
      dates.forEach((date) => {
        fDates.push(
          DatesUtils.returnDateFormatFromLocale(locale, date, SQLdate)
        );
      });
      return fDates;
    }
    const date = DatesUtils.returnDateFormatFromLocale(locale, dates, SQLdate);
    return date;
  }

  public static convertToSQLDate(
    dateString: string,
    locale: string
  ): string | null {
    const sqlDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (sqlDateRegex.test(dateString)) {
      return dateString;
    }
    const locales = {
      fr: fr,
      en: enUS,
    } as { [key: string]: Locale };

    // Define date formats for various locales
    const dateFormats = {
      fr: ['dd/MM/yyyy', 'dd-MM-yyyy'],
      en: ['MM/dd/yyyy', 'MM-dd-yyyy'],
    } as { [key: string]: string[] };

    const selectedLocale = locales[locale] || enUS; // Default to enUS if locale not found
    const formats = dateFormats[locale] || dateFormats['en']; // Default to en formats if not found

    for (const formatString of formats) {
      try {
        const parsedDate = parse(dateString, formatString, new Date(), {
          locale: selectedLocale,
        });
        if (!isNaN(parsedDate.getTime())) {
          return format(parsedDate, 'yyyy-MM-dd');
        }
      } catch (error) {
        // Continue to the next format if parsing fails
      }
    }

    // If no format matched, return null or handle the error accordingly
    return null;
  }

  public static formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Mois commence à 0
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  public static toSQLDate(date: Date, withTime: boolean): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // mois = 0-11
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    // `withTime` est obligatoire : la présence d'une heure tient au schéma du
    // champ. L'heuristique « minuit = date seule » qui le remplaçait quand il
    // manquait faisait dépendre le format de la DONNÉE — un champ date + heure
    // réglé sur minuit perdait son heure, un champ date porteur d'une heure en
    // gagnait une. Tous les appelants passent maintenant par
    // `normalizePayloadValues`, qui lit le schéma.
    if (!withTime) {
      return `${year}-${month}-${day}`;
    }
    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
  }
}
